import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from 'model/Api';
import { User } from 'model/User';
import {
  Role,
  Permission,
  permissions,
  createEmptyPermissions,
  useRole,
} from 'model/Role';
import { generalStyle } from 'helper/Style';
import Layout from 'component/part/Layout';
import BackLink from 'component/part/BackLink';
import FormContent from 'component/part/FormContent';
import OperationTrigger from 'component/part/OperationTrigger';
import Select from 'component/part/Select';
import H2 from 'component/part/H2';
import RadioList from 'component/part/RadioList';

const initialUser: User = {
  id: 0,
  lastName: '',
  firstName: '',
  roleId: 0,
  nameNotationType: 'space',
};

type NameNotationType = {
  value: User['nameNotationType'];
  label: string;
};

const needSalary = (effective?: Role['permissions']) =>
  !!(effective?.workAssign || effective?.workSupervise);

const style = {
  form: {
    ...generalStyle.form,
    ['& input']: {
      maxWidth: '32rem',
    },
  },
  nameArea: {
    ...generalStyle.formRow,
    alignItems: 'start',
    gap: '4rem',
    ['& > div:nth-of-type(1) input']: {
      width: '30rem',
    },
  },
  emailArea: {
    ...generalStyle.formRow,
    gap: '0.5rem',
    ['& input']: {
      width: '20rem !important',
    },
  },
  customizeToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.6rem',
    cursor: 'pointer',
  },
  permissionList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.8rem',
  },
  permissionItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.6rem',
    fontSize: '1.4rem',
    cursor: 'pointer',
  },
};

const MemberEdit: React.FC = () => {
  const api = useApi();
  const { getListAsync: getRoleListAsync } = useRole();
  const navigate = useNavigate();
  const urlParams = useParams<{ id: string }>();
  const userId = parseInt(urlParams.id || '');
  const [user, setUser] = useState<User>(initialUser);
  const [userRole, setUserRole] = useState<Role>();
  const [roles, setRoles] = useState<Role[]>([]);
  const [originalUserRole, setOriginalUserRole] = useState<User['role']>();
  const [emailAccount, setEmailAccount] = useState('');
  const [emailDomain, setEmailDomain] = useState('');
  const [editingPassword, setEditingPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordRepetition, setPasswordRepetition] = useState('');
  const [customizePermissions, setCustomizePermissions] = useState(false);
  const [customPermissions, setCustomPermissions] = useState<Role['permissions']>(createEmptyPermissions());
  const lastName = user.lastName || 'カイタイ';
  const firstName = user.firstName || 'タロウ';
  const nameNotationTypes: NameNotationType[] = [
    { value: 'space', label: `'${lastName} ${firstName}'` },
    { value: 'point', label: `'${lastName}・${firstName}'` },
    { value: 'western', label: `'${firstName}・${lastName}'` },
  ];
  const nameNotationType = nameNotationTypes.find(({ value }) => value === (user.nameNotationType || 'space'));
  // 表示判定や日給要否は customize 中ならカスタム権限、そうでなければ選択中の役職に従う
  const effectivePermissions: Role['permissions'] | undefined =
    customizePermissions ? customPermissions : userRole?.permissions;

  const onChangeUser = <T extends keyof User>(key: T, value: User[T]) => {
    setUser({ ...user, [key]: value });
  };

  const onToggleCustomize = () => {
    if (!customizePermissions) {
      // OFF→ONの遷移時に、選択中役職の権限で初期化する（仕様: 既定値）
      setCustomPermissions(userRole?.permissions || createEmptyPermissions());
    }
    setCustomizePermissions(!customizePermissions);
  };

  const onTogglePermission = (code: Permission) => {
    setCustomPermissions({ ...customPermissions, [code]: !customPermissions[code] });
  };

  const onSubmit = async () => {
    // 役職は登録/更新時に必須
    if (!userRole) return false;
    user.roleId = userRole.id;

    const option: Partial<{ password: string; customRolePermissions: Role['permissions'] }> = {};
    if (effectivePermissions?.login) {
      user.email = `${emailAccount}@${emailDomain}`;
      if (editingPassword) {
        option.password = password;
      }
    }
    if (customizePermissions) {
      option.customRolePermissions = customPermissions;
    }

    const result = await api.user.update(user, option);

    return result.ok && !!result.data;
  };

  // 役職一覧の取得（先に発火）
  useEffect(() => {
    (async () => {
      setRoles(await getRoleListAsync());
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ユーザーの取得・初期化
  useEffect(() => {
    (async () => {
      if (userId) {
        const userResponse = await api.user.get(userId, { with: ['role'] });
        if (userResponse) {
          if (userResponse.role?.permissions.login) {
            const emailParts = (userResponse.email || '').match(/(.*)@(.*)/);
            setEmailAccount(emailParts?.[1] || '');
            setEmailDomain(emailParts?.[2] || '');
          }
          setUser(userResponse);
          setEditingPassword(!userResponse.role?.permissions.login);
          setOriginalUserRole(userResponse.role);

          if (userResponse.role?.isCustom) {
            // カスタム役職を持つユーザー: 個別権限ON、選択は親役職、custom はそのまま
            setCustomizePermissions(true);
            setCustomPermissions(userResponse.role.permissions);
            // 親役職の解決は roles ロード完了後の effect で行う
          } else {
            setCustomizePermissions(false);
            setUserRole(userResponse.role);
          }
        } else {
          setUser({ ...user, id: userId });
        }
      } else {
        setEditingPassword(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // user の役職がカスタムなら、roles ロード後に親役職を選択値として解決する
  useEffect(() => {
    if (
      user.role?.isCustom
      && user.role.parentId != null
      && roles.length
      && (!userRole || userRole.id !== user.role.parentId)
    ) {
      const parent = roles.find((r) => r.id === user.role!.parentId);
      if (parent) setUserRole(parent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.role, roles]);

  return <Layout>
    <BackLink link='/member/list' label='メンバー一覧'></BackLink>
    <H2>{userId ? 'メンバー編集' : 'メンバー登録'}</H2>
    <div css={style.form}>
      <div css={style.nameArea}>
        <div css={generalStyle.form}>
          <FormContent label='姓'>
            <input type='text' value={user.lastName} onChange={(e) => onChangeUser('lastName', e.target.value)} />
          </FormContent>
          <FormContent label='名'>
            <input type='text' value={user.firstName} onChange={(e) => onChangeUser('firstName', e.target.value)} />
          </FormContent>
        </div>
        <FormContent label='氏名表記'>
          <RadioList
            list={nameNotationTypes}
            value={nameNotationType}
            labelKey='label'
            onChange={(item) => onChangeUser('nameNotationType', item.value)}
            direction='column'
          />
        </FormContent>
      </div>
      <FormContent label='役職' required>
        <Select
          list={roles}
          value={roles.find(({ id }) => id === userRole?.id)}
          labelKey='name'
          onChange={(item) => item && setUserRole(item)}
        />
      </FormContent>
      <label css={style.customizeToggle}>
        <input type='checkbox' checked={customizePermissions} onChange={onToggleCustomize} />
        個別に権限を設定する
      </label>
      {customizePermissions && (
        <FormContent label='権限'>
          <div css={style.permissionList}>
            {permissions.flatMap(({ code, label }) => code !== 'root' ? (
              <label key={code} css={style.permissionItem}>
                <input
                  type='checkbox'
                  checked={customPermissions[code]}
                  onChange={() => onTogglePermission(code)}
                />
                <span>{label}</span>
              </label>
            ) : [])}
          </div>
        </FormContent>
      )}
      {effectivePermissions?.login && (
        <Fragment>
          <FormContent label='ユーザー名'>
            <input type='text' value={user.accountName} onChange={(e) => onChangeUser('accountName', e.target.value)} />
          </FormContent>
          <FormContent label='メールアドレス'>
            <div css={style.emailArea}>
              <input type='text' value={emailAccount} onChange={(e) => setEmailAccount(e.target.value)} />
              <span>@</span>
              <input type='text' value={emailDomain} onChange={(e) => setEmailDomain(e.target.value)} />
            </div>
          </FormContent>
          {originalUserRole?.permissions.login && (
            <label>
              <input type='checkbox' onChange={() => setEditingPassword(!editingPassword)} checked={editingPassword} />
              パスワードを再設定する
            </label>
          )}
          {editingPassword && (
            <Fragment>
              <FormContent label='パスワード'>
                <input type='password' value={password} onChange={(e) => setPassword(e.target.value)} />
              </FormContent>
              <FormContent label='パスワード（再入力）'>
                <input type='password' value={passwordRepetition} onChange={(e) => setPasswordRepetition(e.target.value)} />
              </FormContent>
            </Fragment>
          )}
        </Fragment>
      )}
      {needSalary(effectivePermissions) && (
        <Fragment>
          <FormContent label='日給'>
            <input type='number' value={user.salary} onChange={(e) => onChangeUser('salary',  parseInt(e.target.value))} />
          </FormContent>
        </Fragment>
      )}
      <OperationTrigger
        onSubmit={onSubmit}
        onOk={() => navigate('/member/list')}
        buttonText={userId ? '更新' : '登録'}
        objectType='member'
        operationType={userId ? 'edit' : 'create'}
      />
    </div>
  </Layout>;
};

export default MemberEdit;
